import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PLACEHOLDER = `Paste the full sequence here — subject lines, body copy, any follow-ups.

Example:
--- Step 1 ---
Subject: Quick question about {{company}}'s pipeline
Body: Hi {{first_name}}, noticed you're hiring 4 AEs in Q3. Most teams at that growth stage hit a wall when ...

--- Step 2 ---
Subject: Re: Quick question
Body: Following up — figured I'd send the 1-page breakdown ...`;

export default function Audit() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (e) => {
    e?.preventDefault?.();
    if (!text.trim()) {
      setError("Paste your sequence before running an audit.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const r = await api.post("/audit/generate", { sequence_text: text });
      const id = r.data?.audit_id;
      if (!id) throw new Error("No audit_id returned");
      toast.success("Audit ready");
      navigate(`/audit/result/${id}`);
    } catch (err) {
      // 429 from the global cap arrives as {"error": "..."} (NOT detail).
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      if (status === 429 && data.error) {
        setError(data.error);
      } else if (typeof data.detail === "string") {
        setError(data.detail);
      } else if (Array.isArray(data.detail) && data.detail[0]?.msg) {
        setError(data.detail[0].msg);
      } else {
        setError(err.message || "Could not run audit. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const charCount = text.length;

  return (
    <div data-testid="audit-input-page" className="max-w-4xl">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-neutral-500 font-mono">
        <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.8} />
        Sequence auditor
      </div>
      <h1 className="font-display text-4xl font-semibold tracking-tight mt-3 leading-tight">
        Run an audit on your cold sequence
      </h1>
      <p className="mt-3 text-sm text-neutral-400 leading-relaxed max-w-2xl">
        Paste any cold email or full multi-step sequence below. We'll score it
        out of 100 and surface the three highest-leverage rewrites.
      </p>

      {error && (
        <div
          data-testid="audit-error-banner"
          role="alert"
          className="mt-6 flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-300"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={run} className="mt-6">
        <div className="rounded-lg border border-[#1a1a1a] bg-[#0d0d0d] focus-within:border-[#2a2a2a] transition-colors">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={PLACEHOLDER}
            data-testid="audit-sequence-textarea"
            spellCheck={false}
            rows={18}
            className="block w-full resize-y bg-transparent text-[13px] leading-relaxed font-mono text-neutral-200 placeholder:text-neutral-600 px-4 py-3.5 outline-none min-h-[360px]"
          />
          <div className="flex items-center justify-between border-t border-[#1a1a1a] px-4 py-2 text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">
            <span data-testid="audit-counter">
              {wordCount} words &middot; {charCount} chars
            </span>
            <span className="hidden sm:inline">Heuristic engine &middot; v0</span>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between gap-4">
          <p className="text-xs text-neutral-500 max-w-md">
            Reports are saved to your workspace and accessible by their URL.
          </p>
          <button
            type="submit"
            disabled={busy}
            data-testid="audit-run-button"
            className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors px-5 py-2.5 rounded-md font-medium text-sm disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
            ) : (
              <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            )}
            Run audit
          </button>
        </div>
      </form>
    </div>
  );
}
